const formatRoleLabel = (role = "") =>
  role
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const buildDisplayName = (firstName = "", surName = "") =>
  `${firstName || ""} ${surName || ""}`.trim();

const withOptionalProfileImage = (payload, profileImage) => {
  const trimmedProfileImage = profileImage?.trim();

  return trimmedProfileImage
    ? { ...payload, profileImage: trimmedProfileImage }
    : payload;
};

const withOptionalStringFields = (payload, optionalFields = {}) =>
  Object.entries(optionalFields).reduce((nextPayload, [key, value]) => {
    const trimmedValue = value?.trim();

    return trimmedValue ? { ...nextPayload, [key]: trimmedValue } : nextPayload;
  }, payload);

const getFirstFiniteNumber = (...values) => {
  for (const value of values) {
    const parsedValue = Number(value);
    if (Number.isFinite(parsedValue)) {
      return parsedValue;
    }
  }

  return null;
};

const getDerivedStatus = (entity = {}, fallbackStatus = "Active") => {
  if (entity?.status) {
    return entity.status;
  }

  if (entity?.isSuspended === true) {
    return "Suspended";
  }

  if (entity?.isDeleted === true) {
    return "Inactive";
  }

  return fallbackStatus;
};

const getAccountOwnerSource = (group = {}) => {
  const flatOwnerSource =
    group?.firstName ||
    group?.surName ||
    group?.lastName ||
    group?.email ||
    group?.phoneNumber ||
    group?.profileImage ||
    group?.lastLoggedIn !== undefined ||
    group?.platformVisits !== undefined
      ? group
      : null;

  if (group?.accountOwnerData && typeof group.accountOwnerData === "object") {
    return group.accountOwnerData;
  }

  if (group?.accountOwner && typeof group.accountOwner === "object") {
    return group.accountOwner;
  }

  if (group?.owner && typeof group.owner === "object") {
    return group.owner;
  }

  if (group?.superAdminId && typeof group.superAdminId === "object") {
    return group.superAdminId;
  }

  if (
    group?.organisationGroupMember &&
    typeof group.organisationGroupMember === "object"
  ) {
    return group.organisationGroupMember;
  }

  if (group?.superAdmin && typeof group.superAdmin === "object") {
    return group.superAdmin;
  }

  return flatOwnerSource || {};
};

const mapRoleMembers = (members = [], role) =>
  Array.isArray(members)
    ? members.map((member, index) => ({
        id: member?._id || member?.id || `${role}-${index}`,
        name: buildDisplayName(member?.firstName, member?.surName),
        role: formatRoleLabel(role),
        memberOrganisation: member?.memberOrganisation || "Group Office",
        status: getDerivedStatus(member),
        joinedOrganisationGroup:
          member?.joinedOrganisationGroup ||
          member?.createdAt ||
          member?.updatedAt ||
          "",
      }))
    : [];

export const normalizeOrganisationGroup = (group = {}) => {
  const organisationData =
    group.organisationData || group.organisationGroup || group;
  const accountOwnerData = getAccountOwnerSource(group);
  const createdByData = group.createdBy || organisationData?.createdBy || {};
  const groupId =
    group._id ||
    group.id ||
    organisationData?._id ||
    organisationData?.id ||
    "";
  const derivedGroupMembers = [
    ...(accountOwnerData?._id || accountOwnerData?.id || accountOwnerData?.email
      ? [
          {
            id:
              accountOwnerData?._id ||
              accountOwnerData?.id ||
              `${groupId || "group"}-owner`,
            name: buildDisplayName(
              accountOwnerData?.firstName,
              accountOwnerData?.surName,
            ),
            role: "Group Super Admin",
            memberOrganisation: "Group Office",
            status: getDerivedStatus(
              accountOwnerData,
              getDerivedStatus(group, "Active"),
            ),
            joinedOrganisationGroup:
              accountOwnerData?.joinedOrganisationGroup ||
              accountOwnerData?.createdAt ||
              group.createdAt ||
              organisationData?.createdAt ||
              "",
          },
        ]
      : []),
    ...mapRoleMembers(group.admins, "group_admin"),
    ...mapRoleMembers(group.collaborators, "group_collaborator"),
  ];
  const derivedStatus =
    group.status ||
    organisationData?.status ||
    getDerivedStatus(group, "Active");

  return {
    id: groupId,
    name: organisationData?.name || group.name || "",
    description: organisationData?.description || group.description || "",
    postcode: organisationData?.postcode || group.postcode || "",
    address: organisationData?.address || group.address || "",
    organisationEmail: organisationData?.email || group.email || "",
    organisationPhone:
      organisationData?.phoneNumber ||
      organisationData?.phone ||
      group.phoneNumber ||
      group.phone ||
      "",
    code: group.code || group.referenceCode || "",
    status: derivedStatus,
    createdAt:
      group.createdAt ||
      organisationData?.createdAt ||
      accountOwnerData?.createdAt ||
      "",
    lastActive:
      group.lastActive ||
      group.lastActiveDate ||
      group.updatedAt ||
      organisationData?.updatedAt ||
      "",
    owner: {
      role: accountOwnerData?.role || "group_super_admin",
      title: accountOwnerData?.title || "",
      firstName: accountOwnerData?.firstName || "",
      surName: accountOwnerData?.surName || accountOwnerData?.lastName || "",
      email: accountOwnerData?.email || "",
      phoneNumber: accountOwnerData?.phoneNumber || "",
      profileImage: accountOwnerData?.profileImage || "",
      platformVisits: Number(accountOwnerData?.platformVisits || 0),
      lastLoggedIn: accountOwnerData?.lastLoggedIn || "",
      status: getDerivedStatus(accountOwnerData, derivedStatus),
    },
    createdBy: {
      id: createdByData?._id || createdByData?.id || "",
      firstName: createdByData?.firstName || "",
      surName: createdByData?.surName || createdByData?.lastName || "",
      email: createdByData?.email || "",
      role: createdByData?.role || "",
      profileImage: createdByData?.image || createdByData?.profileImage || "",
    },
    memberOrganisations:
      group.memberOrganisations ||
      group.memberOrganizations ||
      group.organisationMembers ||
      [],
    groupMembers:
      group.groupMembers?.length > 0 ? group.groupMembers : derivedGroupMembers,
    teachers: group.teachers || [],
  };
};

export const extractOrganisationGroupList = (responseData) => {
  const candidates = [
    responseData?.data,
    responseData?.data?.organisationGroups,
    responseData?.data?.groups,
    responseData?.data?.list,
    responseData?.organisationGroups,
    responseData?.groups,
    responseData?.list,
  ];

  const listSource = candidates.find((candidate) => Array.isArray(candidate));

  return Array.isArray(listSource)
    ? listSource.map(normalizeOrganisationGroup)
    : [];
};

export const extractOrganisationGroupDetail = (responseData) => {
  const detailSource =
    responseData?.data?.organisationGroup ||
    responseData?.data ||
    responseData?.organisationGroup ||
    responseData;

  return detailSource ? normalizeOrganisationGroup(detailSource) : null;
};

export const buildCreateOrganisationGroupPayload = (values) => ({
  accountOwnerData: withOptionalProfileImage(
    withOptionalStringFields(
      {
        title: values.title.trim(),
        firstName: values.firstName.trim(),
        surName: values.surName.trim(),
        email: values.email.trim().toLowerCase(),
      },
      {
        phoneNumber: values.phoneNumber,
        postcode: values.postcode,
        address: values.address,
      },
    ),
    values.profileImage,
  ),
  organisationData: withOptionalStringFields(
    {
      name: values.organisationGroupName.trim(),
      description: values.description.trim(),
    },
    {
      email: values.organisationEmail,
      phoneNumber: values.organisationPhone,
    },
  ),
});

export const buildUpdateOrganisationGroupPayload = (values) => ({
  accountOwnerData: withOptionalProfileImage(
    withOptionalStringFields(
      {
        title: values.title.trim(),
        firstName: values.firstName.trim(),
        surName: values.surName.trim(),
        // email: values.email.trim().toLowerCase(),
      },
      {
        phoneNumber: values.phoneNumber,
        postcode: values.postcode,
        address: values.address,
      },
    ),
    values.profileImage,
  ),
  organisationData: withOptionalStringFields(
    {
      name: values.organisationGroupName.trim(),
      description: values.description.trim(),
    },
    {
      email: values.organisationEmail,
      phoneNumber: values.organisationPhone,
    },
  ),
});

export const buildOrganisationGroupMemberFallback = (group) => [
  {
    id: `${group.id || "group"}-owner`,
    name: `${group.owner?.firstName || ""} ${group.owner?.surName || ""}`.trim(),
    role: formatRoleLabel(group.owner?.role || "group_super_admin"),
    memberOrganisation: "Group Office",
    status: group.status || "Active",
    joinedOrganisationGroup: group.createdAt || "",
  },
];

export const extractOrganisationGroupListMeta = (
  responseData,
  groups = [],
) => {
  const fallbackMetrics = groups.reduce(
    (accumulator, group) => {
      accumulator.totalGroups += 1;
      accumulator.totalMemberOrganisations +=
        group.memberOrganisations?.length || 0;
      accumulator.totalTeachers += group.teachers?.length || 0;

      if (group.status === "Active") {
        accumulator.activeGroups += 1;
      }

      if (group.status === "Inactive") {
        accumulator.inactiveGroups += 1;
      }

      if (group.status === "Created") {
        accumulator.createdGroups += 1;
      }

      if (group.status === "Suspended") {
        accumulator.suspendedGroups += 1;
      }

      return accumulator;
    },
    {
      totalGroups: 0,
      activeGroups: 0,
      inactiveGroups: 0,
      createdGroups: 0,
      suspendedGroups: 0,
      totalMemberOrganisations: 0,
      totalTeachers: 0,
    },
  );

  const responseSource =
    responseData?.data && !Array.isArray(responseData.data)
      ? responseData.data
      : responseData;

  return {
    ...fallbackMetrics,
    totalGroups:
      getFirstFiniteNumber(
        responseSource?.total,
        responseSource?.count,
        responseSource?.totalGroups,
        responseData?.total,
        responseData?.count,
      ) ?? fallbackMetrics.totalGroups,
    activeGroups:
      getFirstFiniteNumber(
        responseSource?.active,
        responseSource?.activeGroups,
        responseData?.active,
      ) ?? fallbackMetrics.activeGroups,
    inactiveGroups:
      getFirstFiniteNumber(
        responseSource?.inactive,
        responseSource?.inActive,
        responseSource?.inactiveGroups,
        responseData?.inactive,
        responseData?.inActive,
      ) ?? fallbackMetrics.inactiveGroups,
    createdGroups:
      getFirstFiniteNumber(
        responseSource?.created,
        responseSource?.createdGroups,
        responseData?.created,
      ) ?? fallbackMetrics.createdGroups,
    suspendedGroups:
      getFirstFiniteNumber(
        responseSource?.suspended,
        responseSource?.suspendedGroups,
        responseData?.suspended,
      ) ?? fallbackMetrics.suspendedGroups,
  };
};

export const getOrganisationGroupMetrics = (groups = [], listMeta = null) => {
  if (listMeta) {
    return {
      totalGroups: listMeta.totalGroups || 0,
      activeGroups: listMeta.activeGroups || 0,
      inactiveGroups: listMeta.inactiveGroups || 0,
      createdGroups: listMeta.createdGroups || 0,
      suspendedGroups: listMeta.suspendedGroups || 0,
      totalMemberOrganisations: listMeta.totalMemberOrganisations || 0,
      totalTeachers: listMeta.totalTeachers || 0,
    };
  }

  return extractOrganisationGroupListMeta({}, groups);
};

export const normalizeOrganisationGroupMember = (member = {}) => ({
  id: member?._id || member?.id || "",
  role: member?.role || "group_collaborator",
  roleLabel: formatRoleLabel(member?.role || "group_collaborator"),
  title: member?.title || "",
  firstName: member?.firstName || "",
  surName: member?.surName || member?.lastName || "",
  name: buildDisplayName(
    member?.firstName,
    member?.surName || member?.lastName,
  ),
  email: member?.email || "",
  phoneNumber: member?.phoneNumber || "",
  profileImage: member?.profileImage || member?.image || "",
  status: getDerivedStatus(member),
  memberOrganisation: member?.memberOrganisation || "Group Office",
  joinedOrganisationGroup:
    member?.joinedOrganisationGroup ||
    member?.createdAt ||
    member?.updatedAt ||
    "",
});

export const extractOrganisationGroupMemberList = (responseData) => {
  const candidates = [
    responseData?.data,
    responseData?.data?.members,
    responseData?.data?.groupMembers,
    responseData?.data?.list,
    responseData?.members,
    responseData?.groupMembers,
    responseData?.list,
  ];

  const listSource = candidates.find((candidate) => Array.isArray(candidate));

  return Array.isArray(listSource)
    ? listSource.map(normalizeOrganisationGroupMember)
    : [];
};

export const extractOrganisationGroupMemberListMeta = (
  responseData,
  members = [],
) => {
  const responseSource =
    responseData?.data && !Array.isArray(responseData.data)
      ? responseData.data
      : responseData;

  return {
    total:
      getFirstFiniteNumber(
        responseSource?.total,
        responseSource?.count,
        responseData?.total,
        responseData?.count,
      ) ?? members.length,
    page:
      getFirstFiniteNumber(responseSource?.page, responseData?.page) ?? 0,
    limit:
      getFirstFiniteNumber(responseSource?.limit, responseData?.limit) ??
      members.length,
  };
};

export const extractOrganisationGroupMemberDetail = (responseData) => {
  const detailSource =
    responseData?.data?.member ||
    responseData?.data?.organisationGroupMember ||
    responseData?.data ||
    responseData?.member ||
    responseData?.organisationGroupMember ||
    responseData;

  return detailSource ? normalizeOrganisationGroupMember(detailSource) : null;
};

export const buildCreateOrganisationGroupMemberPayload = (values) =>
  withOptionalProfileImage(
    withOptionalStringFields(
      {
        role: values.role,
        title: values.title.trim(),
        firstName: values.firstName.trim(),
        surName: values.surName.trim(),
        email: values.email.trim().toLowerCase(),
      },
      {
        phoneNumber: values.phoneNumber,
        postcode: values.postcode,
        address: values.address,
      },
    ),
    values.profileImage,
  );

export const buildUpdateOrganisationGroupMemberPayload = (values) =>
  withOptionalProfileImage(
    withOptionalStringFields(
      {
        role: values.role,
        title: values.title.trim(),
        firstName: values.firstName.trim(),
        surName: values.surName.trim(),
        // email: values.email.trim().toLowerCase(),
      },
      {
        phoneNumber: values.phoneNumber,
        postcode: values.postcode,
        address: values.address,
      },
    ),
    values.profileImage,
  );
